import React, { useRef, useState, useEffect } from 'react';

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [isListening, setIsListening] = useState(false);

  // Set initial value
  useEffect(() => {
    if (editorRef.current && value !== undefined && !isListening) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, isListening]);

  // Format text with command
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleContentChange();
  };

  // Handle content changes
  const handleContentChange = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      if (html === '<br>') {
        onChange('');
      } else {
        onChange(html);
      }
    }
  };

  // Handle paste to clean up formatting
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleContentChange();
  };

  const toolbarButtonStyle = {
    background: 'transparent',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    color: '#0f084b',
    transition: 'all 0.2s ease',
    minWidth: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const separatorStyle = {
    width: 1,
    height: 20,
    backgroundColor: '#e0e0e0',
    margin: '0 4px'
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        gap: 4,
        padding: 8,
        backgroundColor: '#f9f9f9',
        borderRadius: '8px 8px 0 0',
        border: '1px solid #e0e0e0',
        borderBottom: 'none',
        alignItems: 'center'
      }}>
        <button
          type="button"
          onClick={() => formatText('bold')}
          style={toolbarButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.borderColor = '#0f084b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        
        <button
          type="button"
          onClick={() => formatText('italic')}
          style={toolbarButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.borderColor = '#0f084b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }}
          title="Italic"
        >
          <em>I</em>
        </button>
        
        <button
          type="button"
          onClick={() => formatText('underline')}
          style={toolbarButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.borderColor = '#0f084b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }}
          title="Underline"
        >
          <u>U</u>
        </button>

        <div style={separatorStyle} />

        <button
          type="button"
          onClick={() => formatText('insertUnorderedList')}
          style={toolbarButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.borderColor = '#0f084b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }}
          title="Bullet List"
        >
          • —
        </button>
        
        <button
          type="button"
          onClick={() => formatText('insertOrderedList')}
          style={toolbarButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.borderColor = '#0f084b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }}
          title="Numbered List"
        >
          1.
        </button>

        <div style={separatorStyle} />

        <button
          type="button"
          onClick={() => formatText('removeFormat')}
          style={{...toolbarButtonStyle, fontSize: 12}}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.borderColor = '#0f084b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }}
          title="Clear Formatting"
        >
          Clear
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleContentChange}
        onPaste={handlePaste}
        onFocus={() => setIsListening(true)}
        onBlur={() => setIsListening(false)}
        style={{
          minHeight: 150,
          padding: 12,
          border: '1px solid #e0e0e0',
          borderRadius: '0 0 8px 8px',
          backgroundColor: 'white',
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: 14,
          color: '#0f084b',
          lineHeight: 1.6,
          outline: 'none',
          overflowY: 'auto',
          maxHeight: 300
        }}
        data-placeholder={placeholder || 'Enter text here...'}
      />

      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #999;
          pointer-events: none;
          display: block;
        }
        [contenteditable] ul,
        [contenteditable] ol {
          margin: 8px 0;
          padding-left: 24px;
        }
        [contenteditable] li {
          margin: 4px 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
